import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { I18nextProvider } from 'react-i18next';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-image-crop/dist/ReactCrop.css';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import dayjs from 'dayjs';
import Router from './routes/Router';
import { store, persistor } from './redux/store';
import { getSelectedCommunity } from './hooks/community/useSelectedCommunity';
import communityService from './services/community/communityService';
import i18n from './locales/i18n';
import inviteService from './services/community/inviteService';
import ScrollToTop from './hooks/effects/ScrollToTop';
import connectionService from './services/community/connectionService';
import OnboardingRide from './views/components/misc/OnboardingRide';
import { wipeSelectedCommunity, wipeSelectedBusiness } from './redux/slices/applicationSlice';
import { normalizeLanguage } from './types/language';

const syncDayjsLocale = (): void => {
  const language = normalizeLanguage(store.getState().application.language);
  dayjs.locale(language);
};
syncDayjsLocale();
store.subscribe(syncDayjsLocale);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <Provider store={store}>
    <I18nextProvider i18n={i18n}>
      <PersistGate loading={null} persistor={persistor}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <BrowserRouter>
            <Router />
            <ScrollToTop />
            <div>
              <OnboardingRide />
              <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
                className="mx-auto w-full"
                progressClassName="invisible"
                style={{
                  width: 'full',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                toastStyle={{
                  backgroundColor: 'black',
                  color: 'white',
                  width: 'fit-content',
                  borderRadius: '1.5rem',
                  fontWeight: '600',
                }}
              />
            </div>
          </BrowserRouter>
        </LocalizationProvider>
      </PersistGate>
    </I18nextProvider>
  </Provider>,
);

window.addEventListener('load', () => {
  const { application } = store.getState();

  if (!application.isAuthenticated) return;

  communityService.fetchCommunity().then(() => {
    const { application: app } = store.getState();

    const communities = app.communities ?? [];
    const businesses = app.businesses ?? [];

    const selectedCommunityValid =
      app.selectedCommunity != null &&
      communities.some((community: any) => community.id === app.selectedCommunity);

    const selectedBusinessValid =
      app.selectedBusiness != null &&
      businesses.some((business: any) => business.businessId === app.selectedBusiness);

    const selectedDashboardValid =
      app.selectedDashboard != null &&
      (communities.some((community: any) => community.id === app.selectedDashboard) ||
        businesses.some(
          (business: any) =>
            business.dashboard && business.dashboard.dashboardId === app.selectedDashboard,
        ));

    const selectionIsValid =
      selectedDashboardValid || selectedCommunityValid || selectedBusinessValid;

    if (!selectionIsValid) {
      store.dispatch(wipeSelectedCommunity());
      store.dispatch(wipeSelectedBusiness());
      return;
    }

    if (getSelectedCommunity()) {
      inviteService.fetchInvite();
      connectionService.getConnections();
    }
  });
});
